/**
 * Store for managing members data
 *
 * IMPORTANT REACTIVITY NOTES:
 * 1. Always use $patch for state updates to ensure proper reactivity
 * 2. Cross-store dependencies are managed through subscriptions
 * 3. Tracking properties (_lastSyncedType, _lastSyncedCaptainId) prevent infinite loops
 * 4. Captain and SubKrewe relationships are maintained bidirectionally
 */

import type { DocumentData, DocumentReference, Query, QuerySnapshot } from 'firebase/firestore'
import { addDoc, and, collection, deleteDoc, deleteField, doc, getDoc, getDocs, onSnapshot, orderBy, query, serverTimestamp, updateDoc, where } from 'firebase/firestore'
import { defineStore } from 'pinia'
import { useCurrentUser } from 'vuefire'
import { useMembershipNotifications } from '~/composables/notifications/useMembershipNotifications.js'
import { useSubscriptions } from '~/composables/useSubscriptions.js'
import type { Member, MemberFirestoreData } from '~/shared/types/member.js'
import { deepTrim } from '~/shared/utils/sanitize.js'

export const useSubKrewesStore = defineStore('subkrewes', {
  state: () => ({
    // Store subkrewes by document path for O(1) lookups
    subkrewes: {} as Record<string, SubKrewe>,
    loading: false,
    initialized: false,
  }),

  getters: {
    // Convert map to array for components that expect arrays
    subkrewesArray(): SubKrewe[] {
      return Object.values(this.subkrewes)
    },

    // Get a subkrewe by ID
    getSubkreweById(): (id: string) => SubKrewe | undefined {
      return (id: string) => {
        const path = `subkrewes/${id}`
        return this.subkrewes[path]
      }
    },

    // Get members of a specific subkrewe
    getMembersForSubKrewe(): (subkreweId: string) => Member[] {
      return (subkreweId: string) => {
        const path = `subkrewes/${subkreweId}`
        return this.subkrewes[path]?.members || []
      }
    },

    availableSubkrewes(): SubKrewe[] {
      return this.subkrewesArray.filter(sk =>
        !sk.captains || sk.captains.length === 0,
      )
    },
  },

  actions: {
    /**
     * Convert Firestore document data to a SubKrewe object
     * With streamlined captain resolution
     */
    convertToSubKrewe(docId: string, data: SubKreweFirestoreData, resolveCaptains = true): SubKrewe {
      // Handle date conversions
      const created_at = data.created_at ? data.created_at : new Date()
      const updated_at = data.updated_at ? data.updated_at : new Date()

      // Create the basic subkrewe
      const subkrewe: SubKrewe = {
        id: docId,
        name: data.name,
        description: data.description,
        captains: [], // Initialize as empty array
        members: [], // Initialize empty members array
        approved: data.approved,
        created_at,
        updated_at,
        // Other fields remain the same
        estimated_members: data.estimated_members,
        expected: data.expected, // Using new expected structure
        admission_policy: data.admission_policy,
        admission_restrictions: data.admission_restrictions,
        contraption: data.contraption,
        contraption_info: data.contraption_info,
        music: data.music,
        music_other: data.music_other,
        rolling_next_parade: data.rolling_next_parade,
        declined_by: data.declined_by,
        decline_reason: data.decline_reason,
        email: data.email, // Add the email field
        abandoned: data.abandoned,
        abandoned_at: data.abandoned_at,
        abandoned_by: data.abandoned_by,
        abandonment_reason: data.abandonment_reason,
        visible_in_browser: data.visible_in_browser,
        sponsored: data.sponsored,
        specialty: data.specialty || [], // Map specialty from Firestore
        nerd_herder_cap: data.nerd_herder_cap,
        discount_percent: data.discount_percent,
        discount_cap: data.discount_cap,
      }

      // If no captains reference or not resolving, return as is
      if (!data.captains || !resolveCaptains) {
        return subkrewe
      }

      // Get the members store once
      const membersStore = useMembersStore()

      // Resolve captains - fetch each captain by ID
      if (Array.isArray(data.captains)) {
        // For each captain reference, fetch the actual member
        data.captains.forEach((captainRef) => {
          if (!captainRef || typeof captainRef !== 'object' || !('id' in captainRef) || typeof captainRef.id !== 'string') {
            console.warn('[SubKrewes] Skipping invalid captain reference', captainRef)
            return
          }
          const captainId = captainRef.id as string
          if (!captainId) {
            console.warn('[SubKrewes] Empty captain id in reference, skipping')
            return
          }

          const subkrewePath = `subkrewes/${subkrewe.id}`
          // Try to resolve synchronously from members store (for tests and already-loaded members)
          const existingCaptain = membersStore.members[`members/${captainId}`]
          if (existingCaptain) {
            existingCaptain.subkrewe = subkrewe
            // Add to returned subkrewe object immediately
            if (!subkrewe.captains.some(c => c.id === existingCaptain.id)) {
              subkrewe.captains.push(existingCaptain)
            }
            // Also patch stored subkrewe if present
            const storedSubkrewe = this.subkrewes[subkrewePath]
            if (storedSubkrewe && !storedSubkrewe.captains.some(c => c.id === existingCaptain.id)) {
              this.$patch((state) => {
                if (state.subkrewes[subkrewePath]) {
                  state.subkrewes[subkrewePath].captains.push(existingCaptain)
                }
              })
            }
            return
          }

          // Otherwise fetch the captain asynchronously
          membersStore.fetchMember(captainId).then((captain) => {
            if (captain) {
              captain.subkrewe = subkrewe
              const storedSubkrewe = this.subkrewes[subkrewePath]
              if (storedSubkrewe) {
                if (!storedSubkrewe.captains.some(c => c.id === captain.id)) {
                  this.$patch((state) => {
                    if (state.subkrewes[subkrewePath]) {
                      state.subkrewes[subkrewePath].captains.push(captain)
                    }
                  })
                }
              }
            }
          }).catch((error) => {
            console.error(`[SubKrewes] Error fetching captain ${captainId}:`, error)
          })
        })
      }

      return subkrewe
    },

    /**
     * Convert a SubKrewe object to Firestore-compatible data format
     * No change needed here since member references are managed separately
     */
    convertFirestoreData(subkrewe: SubKrewe): Partial<SubKreweFirestoreData> {
      const db = useFirestore()

      // Create the base firestore data object
      const firestoreData: Partial<SubKreweFirestoreData> = {
        // Basic fields
        name: subkrewe.name,
        description: subkrewe.description,
        updated_at: serverTimestamp(),

        // Additional fields
        approved: subkrewe.approved,
        estimated_members: ensureNumber(subkrewe.estimated_members),
        expected: subkrewe.expected, // Using new expected structure
        admission_policy: subkrewe.admission_policy,
        admission_restrictions: subkrewe.admission_restrictions,
        contraption: subkrewe.contraption,
        contraption_info: subkrewe.contraption_info,
        music: subkrewe.music,
        music_other: subkrewe.music_other,
        rolling_next_parade: subkrewe.rolling_next_parade,
        declined_by: subkrewe.declined_by,
        decline_reason: subkrewe.decline_reason,
        email: subkrewe.email, // Add the email field
        abandoned: subkrewe.abandoned,
        abandoned_at: subkrewe.abandoned_at,
        abandoned_by: subkrewe.abandoned_by,
        abandonment_reason: subkrewe.abandonment_reason,
        visible_in_browser: subkrewe.visible_in_browser,
        sponsored: subkrewe.sponsored,
        nerd_herder_cap: subkrewe.nerd_herder_cap,
        discount_percent: subkrewe.discount_percent,
        discount_cap: subkrewe.discount_cap,
      }

      // Remove any undefined fields, because Firestore doesn't like them
      for (const key in firestoreData) {
        if (firestoreData[key] === undefined) {
          firestoreData[key] = deleteField()
        }
      }

      // Handle captains reference - convert array of objects to array of DocumentReferences
      if (subkrewe.captains && subkrewe.captains.length > 0) {
        firestoreData.captains = subkrewe.captains.map(captain =>
          doc(db, 'members', captain.id),
        )
      }
      else {
        firestoreData.captains = deleteField()
      }

      return firestoreData
    },

    /**
     * Ensure subkrewes are loaded
     */
    async ensureSubKrewesLoaded(): Promise<void> {
      if (this.initialized && Object.keys(this.subkrewes).length > 0) {
        return
      }

      await this.fetchSubKrewes()
    },

    /**
     * Fetch all subkrewes
     */
    async fetchSubKrewes(loadMembers = false): Promise<SubKrewe[]> {
      this.loading = true

      try {
        // First ensure all captains are loaded
        const membersStore = useMembersStore()
        await membersStore.fetchCaptains()

        const db = useFirestore()
        const querySnapshot = await getDocs(
          query(collection(db, 'subkrewes'), orderBy('name')),
        )

        // Clear existing data if we're doing a full refresh
        if (Object.keys(this.subkrewes).length === 0) {
          this.subkrewes = {}
        }

        // Convert and store each subkrewe
        querySnapshot.forEach((doc) => {
          const data = doc.data() as SubKreweFirestoreData
          const subkrewe = this.convertToSubKrewe(doc.id, data)
          this.subkrewes[doc.ref.path] = subkrewe
        })

        // If requested, load members for each subkrewe
        if (loadMembers) {
          const loadPromises = Object.values(this.subkrewes).map(
            subkrewe => this.loadSubkreweMembers(subkrewe),
          )
          await Promise.all(loadPromises)
        }

        this.initialized = true
        return Object.values(this.subkrewes)
      }
      catch (error) {
        console.error('Error fetching subkrewes:', error)
        return []
      }
      finally {
        this.loading = false
      }
    },

    /**
     * Fetch subkrewes with members preloaded
     */
    async fetchSubKrewesWithMembers(): Promise<SubKrewe[]> {
      return await this.fetchSubKrewes(true)
    },

    /**
    * Fetch a subkrewe directly from a DocumentReference
    * This is used primarily for resolving references from other documents
    */
    async fetchSubKreweByRef(
      subkreweRef: DocumentReference | { path: string } | null | undefined,
      forceRefresh = false,
    ): Promise<SubKrewe | null> {
      try {
        if (!subkreweRef) return null

        // Type guard for Firestore DocumentReference (duck-typing minimal props)
        const isDocumentReference = (val: unknown): val is DocumentReference => {
          return !!val && typeof val === 'object'
            && 'path' in (val as Record<string, unknown>)
            && typeof (val as Record<string, unknown>).path === 'string'
            && 'id' in (val as Record<string, unknown>)
            && typeof (val as Record<string, unknown>).id === 'string'
        }

        let workingRef: DocumentReference | null = null

        if (isDocumentReference(subkreweRef)) {
          workingRef = subkreweRef
        }
        else if (typeof subkreweRef === 'object' && typeof subkreweRef.path === 'string') {
          // Only support paths of form 'subkrewes/<id>' for reconstruction
          const match = /^subkrewes\/(.+)$/.exec(subkreweRef.path.trim())
          if (!match) {
            console.warn('[SubKrewes] Unsupported custom path for subkreweRef', subkreweRef.path)
            return null
          }
          try {
            const db = useFirestore()
            const id = match[1]
            workingRef = doc(db, `subkrewes/${id}`)
          }
          catch (e) {
            console.error('[SubKrewes] Failed to reconstruct DocumentReference from path', subkreweRef.path, e)
            return null
          }
        }
        else {
          console.warn('[SubKrewes] fetchSubKreweByRef called with unsupported reference object', subkreweRef)
          return null
        }

        const path = workingRef.path

        // Check if already in store and not forcing refresh
        if (!forceRefresh && this.subkrewes[path]) {
          return this.subkrewes[path]
        }

        // Fetch from Firestore
        const docSnap = await getDoc(workingRef)

        if (!docSnap.exists()) {
          console.warn(`SubKrewe at path ${path} does not exist`)
          return null
        }

        // Ensure captains are available in members store before converting
        const membersStore = useMembersStore()
        await membersStore.fetchCaptains()

        const data = docSnap.data() as SubKreweFirestoreData
        const subkrewe = this.convertToSubKrewe(workingRef.id, data)

        // Store in map by path
        this.$patch((state) => {
          state.subkrewes[path] = subkrewe
        })

        return subkrewe
      }
      catch (error) {
        console.error(`Error fetching subkrewe by reference:`, error)
        return null
      }
    },

    /**
   * Fetch a single subkrewe by ID
   */
    async fetchSubKrewe(id: string, forceRefresh = false): Promise<SubKrewe | null> {
      try {
        // Check if already in store and not forcing refresh
        const path = `subkrewes/${id}`
        if (!forceRefresh && this.subkrewes[path]) {
          return this.subkrewes[path]
        }

        const db = useFirestore()
        const subkreweRef = doc(db, 'subkrewes', id)
        // Defensive check for mocked environments where doc() may return undefined
        const refUnknown = subkreweRef as unknown as { path?: unknown }
        if (!refUnknown || typeof refUnknown !== 'object' || typeof refUnknown.path !== 'string') {
          console.error('[SubKrewes] Invalid document reference returned for id:', id)
          return null
        }

        // Use our new method to handle the fetching
        return await this.fetchSubKreweByRef(subkreweRef, forceRefresh)
      }
      catch (error) {
        console.error(`Error fetching subkrewe ${id}:`, error)
        return null
      }
    },

    /**
     * Update a SubKrewe in Firestore using a complete SubKrewe object
     */
    async updateSubKrewe(subkrewe: SubKrewe, updates: Partial<SubKrewe> = {}): Promise<boolean> {
      try {
        if (!subkrewe || !subkrewe.id) {
          console.error('Cannot update subkrewe: invalid subkrewe object or missing ID')
          return false
        }

        // Create a merged object for Firestore update
        const db = useFirestore()
        const subkreweRef = doc(db, 'subkrewes', subkrewe.id)
        // Sanitize updates before converting to Firestore format
        const sanitizedUpdates = deepTrim({ ...updates })
        const firestoreData = this.convertFieldsToFirestore(sanitizedUpdates)

        // Update the document in Firestore
        await updateDoc(subkreweRef, {
          ...firestoreData,
          updated_at: serverTimestamp(),
        })

        // Update local state in a clean way that preserves reactivity
        const path = `subkrewes/${subkrewe.id}`

        // Use Pinia's patch method to update specific fields
        this.$patch((state) => {
          if (state.subkrewes[path]) {
            // Only update the fields that have changed
            Object.keys(sanitizedUpdates).forEach((key) => {
              if (key !== 'captains' && key !== 'members' && state.subkrewes[path]) {
                state.subkrewes[path][key] = sanitizedUpdates[key]
              }
            })

            // Always update timestamp
            state.subkrewes[path].updated_at = new Date()
          }
        })

        return true
      }
      catch (error) {
        console.error(`Error updating subkrewe ${subkrewe.id}:`, error)
        return false
      }
    },
    convertFieldsToFirestore(data: Partial<SubKrewe>): Partial<SubKreweFirestoreData> {
      const result: Partial<SubKreweFirestoreData> = {}

      Object.entries(data).forEach(([key, value]) => {
        // Skip functions, complex objects and undefined values
        if (
          typeof value !== 'function'
          && value !== undefined
          && key !== 'captains'
          && key !== 'members'
          && !key.startsWith('_')
        ) {
          // This type assertion is necessary at the boundary with Firestore
          result[key] = value
        }
      })

      return result
    },
    /**
     * Load a SubKrewe's members from Firestore into memory
     *
     * IMPORTANT: This does NOT update the database. It only populates the in-memory
     * members[] array by querying for all members that reference this subkrewe.
     *
     * The members[] array is not stored in the database - it's a runtime convenience
     * for UI components that need to display member lists or counts.
     */
    async loadSubkreweMembers(subkrewe: SubKrewe): Promise<Member[]> {
      try {
        if (!subkrewe || !subkrewe.id) {
          console.error('Cannot load members: invalid subkrewe object or missing ID')
          return []
        }

        const db = useFirestore()
        const subkreweRef = doc(db, 'subkrewes', subkrewe.id)
        const membersStore = useMembersStore()

        // Query for all members with this subkrewe reference
        const q = query(
          collection(db, 'members'),
          where('subkrewe', '==', subkreweRef),
        )

        const querySnapshot = await getDocs(q)
        const members: Member[] = []

        // Process each member document
        for (const doc of querySnapshot.docs) {
          const data = doc.data() as MemberFirestoreData

          // Convert to Member object and add to members array
          const member = membersStore.convertToMember(doc.id, data, false)

          // Set the member's subkrewe reference
          member.subkrewe = subkrewe

          // Store in members store
          const path = `members/${member.id}`
          membersStore.members[path] = member

          // Add to our results array
          members.push(member)
        }

        // Update the subkrewe's members array
        const path = `subkrewes/${subkrewe.id}`
        if (this.subkrewes[path]) {
          this.subkrewes[path].members = members
        }

        return members
      }
      catch (error) {
        console.error(`Error loading members for subkrewe ${subkrewe.id}:`, error)
        return []
      }
    },

    /**
     * Assign a member as captain of a subkrewe
     * Handles all aspects of the relationship change
     */

    async assignCaptainToSubKrewe(subkrewe: SubKrewe, newCaptain: Member): Promise<boolean> {
      try {
        if (!subkrewe || !newCaptain) {
          console.error('Invalid subkrewe or captain')
          return false
        }

        const membersStore = useMembersStore()

        // Check if they're already a captain in this subkrewe
        if (subkrewe.captains && subkrewe.captains.some(captain => captain.id === newCaptain.id)) {
          return true // Already assigned, nothing to do
        }

        // Create a fresh copy of the subkrewe to avoid reference issues
        const updatedSubkrewe = { ...subkrewe }

        // Initialize captains array if it doesn't exist
        if (!updatedSubkrewe.captains) {
          updatedSubkrewe.captains = []
        }

        // Set up the member as a captain
        const updatedNewCaptain = {
          ...newCaptain,
          member_type: 'captain',
          subkrewe: subkrewe,
        } as Member

        // Add to members array if not already there
        if (!updatedSubkrewe.members) {
          updatedSubkrewe.members = []
        }

        // First remove to avoid duplicates
        updatedSubkrewe.members = updatedSubkrewe.members.filter(m => m.id !== newCaptain.id)
        updatedSubkrewe.members.push(updatedNewCaptain)

        // Handle old subkrewe relationship if needed
        const oldSubKrewe = newCaptain.subkrewe
        if (oldSubKrewe && oldSubKrewe.id !== subkrewe.id) {
          const oldSubkrewePath = `subkrewes/${oldSubKrewe.id}`
          if (this.subkrewes[oldSubkrewePath]) {
            // Remove from old subkrewe's members array
            this.subkrewes[oldSubkrewePath].members
              = this.subkrewes[oldSubkrewePath].members?.filter(m => m.id !== newCaptain.id) || []

            // Remove from old subkrewe's captains array if needed
            if (newCaptain.member_type === 'captain') {
              this.subkrewes[oldSubkrewePath].captains
                = this.subkrewes[oldSubkrewePath].captains.filter(c => c.id !== newCaptain.id)
            }
          }
        }

        // Update in-memory state
        const subkrewePath = `subkrewes/${subkrewe.id}`
        const captainPath = `members/${newCaptain.id}`
        membersStore.members[captainPath] = updatedNewCaptain

        // Get Firestore references
        const db = useFirestore()
        const captainRef = doc(db, 'members', newCaptain.id)
        const subkreweRef = doc(db, 'subkrewes', subkrewe.id)

        // IMPORTANT FIX: Update captain first to satisfy security rules
        // 1. First update the member to be a captain
        await updateDoc(captainRef, membersStore.convertToFirestoreData(updatedNewCaptain))

        // Update subkrewe's captains array
        updatedSubkrewe.captains.push(updatedNewCaptain)

        // 2. Then update the SubKrewe with the captain reference
        // A small delay helps ensure Firestore consistency

        await updateDoc(subkreweRef, {
          captains: updatedSubkrewe.captains.map(c => doc(db, 'members', c.id)),
          updated_at: serverTimestamp(),
        })

        // Update local state after all Firestore operations complete
        this.subkrewes[subkrewePath] = updatedSubkrewe

        return true
      }
      catch (error) {
        console.error(`Error assigning captain to subkrewe ${subkrewe?.id}:`, error)
        return false
      }
    },

    /**
     * Remove a captain from a subkrewe
     * Handles all relationship changes
     */
    async removeCaptainFromSubKrewe(subkrewe: SubKrewe, captain: Member, keepAsMember = false): Promise<boolean> {
      try {
        // Verify captain is in the captains array
        if (!subkrewe.captains || !subkrewe.captains.some(c => c.id === captain.id)) {
          console.error('Captain not found in subkrewe captains list')
          return false
        }

        const membersStore = useMembersStore()

        // Update in-memory first for immediate reactivity
        this.$patch((state) => {
          const subkrewePath = `subkrewes/${subkrewe.id}`
          if (state.subkrewes[subkrewePath]) {
            // Remove from captains array
            state.subkrewes[subkrewePath].captains
              = state.subkrewes[subkrewePath].captains.filter(c => c.id !== captain.id)

            // Remove from members array if not keeping as member
            if (!keepAsMember) {
              state.subkrewes[subkrewePath].members
                = state.subkrewes[subkrewePath].members?.filter(m => m.id !== captain.id)
            }
          }
        })

        // Then update Firestore
        const db = useFirestore()
        const subkreweRef = doc(db, 'subkrewes', subkrewe.id)

        // Get current captains excluding the one being removed
        const updatedCaptains = subkrewe.captains.filter(c => c.id !== captain.id)

        // Prepare updates for Firestore with proper typing
        const updates: Partial<SubKreweFirestoreData> = {
          updated_at: serverTimestamp(),
        }

        if (updatedCaptains.length > 0) {
          // Still have some captains left
          updates.captains = updatedCaptains.map(c => doc(db, 'members', c.id))
        }
        else {
          // No captains left
          updates.captains = deleteField()
        }

        // If not keeping as member, also update the members array in Firestore
        if (!keepAsMember) {
          const updatedMembers = subkrewe.members?.filter(m => m.id !== captain.id) || []
          updates.members = updatedMembers.length > 0
            ? updatedMembers.map(m => doc(db, 'members', m.id))
            : deleteField()
        }

        // Cast to any only at the Firestore boundary
        await updateDoc(subkreweRef, updates)

        // Update the captain document
        const captainRef = doc(db, 'members', captain.id)
        await updateDoc(captainRef, {
          member_type: 'member', // Demote back to regular member
          ...(keepAsMember ? {} : { subkrewe: deleteField() }),
          updated_at: serverTimestamp(),
        })

        // Update the captain in memory
        membersStore.$patch((state) => {
          const captainPath = `members/${captain.id}`
          if (state.members[captainPath]) {
            state.members[captainPath].member_type = 'member'
            if (!keepAsMember) {
              state.members[captainPath].subkrewe = null
            }
          }
        })

        return true
      }
      catch (error) {
        console.error(`Error removing captain from subkrewe ${subkrewe.id}:`, error)
        return false
      }
    },

    /**
     * Add a regular member to a subkrewe
     * Simpler than captain assignment
     * @param suppressNotifications If true, no notifications will be sent (useful when called from tasks)
     * @param actingUserId Optional ID of the user performing the action (for notifications)
     */
    async addMemberToSubKrewe(
      subkrewe: SubKrewe,
      member: Member,
      suppressNotifications = false,
      actingUserId?: string,
    ): Promise<boolean> {
      // Cannot add a captain from another subkrewe
      if (member.member_type === 'captain' && member.subkrewe && member.subkrewe.id !== subkrewe.id) {
        console.error('Cannot add a captain from another subkrewe directly')
        return false
      }

      // Use the member store's changeMemberSubKrewe method
      const membersStore = useMembersStore()
      const success = await membersStore.changeMemberSubKrewe(
        member,
        subkrewe,
        suppressNotifications,
        actingUserId,
      )

      // If successful and notifications aren't suppressed, send notifications to captains
      if (success && !suppressNotifications) {
        try {
          console.warn('Sending notifications for new member joining:', member.id, subkrewe.id)
          const { createMemberJoinedNotificationForCaptains } = useMembershipNotifications()
          const notificationIds = await createMemberJoinedNotificationForCaptains(
            member.id,
            subkrewe.id,
            actingUserId, // Pass the acting user ID to exclude them from notifications
          )
          console.warn('Join notifications sent:', notificationIds)
        }
        catch (err) {
          console.error('Error sending captain notifications for new member:', err)
          // Don't fail the operation if notifications fail
        }
      }

      return success
    },

    /**
     * Remove a member from a subkrewe
     * @param actingUserId Optional ID of the user performing the removal (for notifications)
     * @param reason Optional reason for the removal (e.g., "banned", "removed by captain", "voluntary")
     */
    async removeMemberFromSubKrewe(subkrewe: SubKrewe, member: Member, actingUserId?: string, reason?: string): Promise<boolean> {
      // Cannot remove a captain directly
      if (member.member_type === 'captain' && subkrewe.captains?.some(c => c.id === member.id)) {
        console.error('Cannot remove a captain directly - use removeCaptainFromSubKrewe')
        return false
      }

      // Get the acting user ID (defaults to current user if provided by plugin; avoid hard dependency in tests)
      let actualActingUserId = actingUserId
      if (!actualActingUserId) {
        try {
          // This may warn if VueFireAuth module isn't installed; wrap in try/catch to avoid noisy test output
          const current = useCurrentUser()
          actualActingUserId = current?.value?.uid
        }
        catch {
          // ignore
        }
      }

      // If still no valid user ID, use a system identifier to avoid the warning
      if (!actualActingUserId) {
        console.warn('No acting user ID available for member removal - using system identifier')
        actualActingUserId = 'system-action'
      }

      // Use the member store's method
      const membersStore = useMembersStore()
      const success = await membersStore.changeMemberSubKrewe(member, null)

      if (success) {
        try {
          // 1. Send notifications to captains (except the acting user if they're a captain)
          if (subkrewe.captains && subkrewe.captains.length > 0) {
            // Filter out the acting user from recipients
            const captainsToNotify = subkrewe.captains.filter(c => c.id !== actualActingUserId)

            if (captainsToNotify.length > 0) {
              console.warn('Sending notifications for member leaving (to captains):', member.id, subkrewe.id)
              const { createMemberLeftNotificationForCaptains } = useMembershipNotifications()
              console.warn('Reason for removal:', reason)
              const notificationIds = await createMemberLeftNotificationForCaptains(
                member.id,
                subkrewe.id,
                actualActingUserId, // Pass the acting user ID to exclude them from notifications
                reason, // Pass the reason to the notification
              )
              console.warn('Leave notifications sent to captains:', notificationIds)
            }
            else {
              console.warn('No captains to notify (acting user is the only captain)')
            }
          }

          // 2. Send notification to the member with appropriate tone
          const { createRemovedFromSubKreweNotification, createSelfLeftSubKreweNotification } = useMembershipNotifications()
          let memberNotifId: string
          if (actualActingUserId === member.id) {
            // Self-initiated leave: softer copy
            memberNotifId = await createSelfLeftSubKreweNotification(member.id, subkrewe.name)
          }
          else {
            // Removed by captain/admin/system: explicit removal copy
            memberNotifId = await createRemovedFromSubKreweNotification(
              member.id,
              subkrewe.name,
              actualActingUserId,
              reason,
            )
          }
          console.warn('Member leave/removal notification sent to member:', memberNotifId)

          // 3. Fraud signal to admins if member had a pooled (bulk) ticket for this SubKrewe in the current year
          try {
            const config = useRuntimeConfig()
            const year = String(config.public?.nextParadeYear ?? 2026)

            const purchases = (member.purchases ?? {}) as Record<string, unknown>
            const yearPurchases = (purchases[year] ?? {}) as Record<string, unknown>
            const membership = Array.isArray((yearPurchases as { membership?: unknown }).membership)
              ? (yearPurchases as { membership?: unknown[] }).membership as Array<Record<string, unknown>>
              : []
            const hadPooledFromThisSubkrewe = membership.some(it => it?.pooled === true && it?.subkreweId === subkrewe.id)

            // Debug removed

            if (hadPooledFromThisSubkrewe) {
              const { useAdminNotifications } = await import('~/composables/notifications/useAdminNotifications.js')
              const adminNotif = useAdminNotifications()
              const title = 'Pooled member left SubKrewe'
              const body = `${member.name || member.id} left ${subkrewe.name} (${subkrewe.id}) after redeeming a pooled ticket for ${year}.` + (reason ? ` Reason: ${reason}.` : '')
              await adminNotif.createAdminAlertNotification(title, body, { priority: 'high', linkType: 'member', linkTarget: member.id, sendEmail: true })
            }
          }
          catch (e) {
            console.error('Error emitting fraud signal notification:', e)
          }
        }
        catch (err) {
          console.error('Error sending notifications for member leaving:', err)
          // Don't fail the operation if notifications fail
        }
      }

      return success
    },

    updateSubKrewesArrayState() {
      // This is a trick to force Vue's reactivity system to recognize the change
      // by replacing the array reference
      const _temp = [...this.subkrewesArray]
      // The actual action is done in the getter, this just forces a refresh
    },

    // Add these functions to implement SubKrewe creation and captain assignment
    /**
     * Create a new SubKrewe and assign member as captain
     */
    createSubKrewe: async function (subkreweData: Partial<SubKrewe>, member: Member, options?: { assignCaptain?: boolean, autoApproved?: boolean }): Promise<string | null> {
      try {
        const membersStore = useMembersStore()
        const tasksStore = useTasksStore() // Add this line to import the tasks store
        const db = useFirestore()

        // 1. Create the SubKrewe in Firestore first
        const subkrewesRef = collection(db, 'subkrewes')

        const assignCaptain = options?.assignCaptain !== false
        const autoApproved = options?.autoApproved === true

        // Sanitize incoming form data (trim strings) before building Firestore payload
        const sanitized = deepTrim({ ...subkreweData })

        // Format the data for Firestore
        const now = serverTimestamp()
        const subkreweDocData: Partial<SubKreweFirestoreData> = {
          name: sanitized.name,
          description: sanitized.description || '',
          approved: sanitized.approved || (autoApproved ? 'Yes' : 'Pending'), // Allow override or auto-approve
          created_at: now,
          updated_at: now,
          estimated_members: ensureNumber(sanitized.estimated_members),
          // Remove secondary field as we're replacing it with captains
          admission_policy: sanitized.admission_policy || 'open',
          admission_restrictions: sanitized.admission_restrictions || '',
          contraption: sanitized.contraption || false,
          contraption_info: sanitized.contraption_info || '',
          music: sanitized.music || 'none',
          music_other: sanitized.music_other || '',
          rolling_next_parade: sanitized.rolling_next_parade || 'Yes',
          visible_in_browser: sanitized.visible_in_browser, // Add this line to save visible_in_browser
          specialty: sanitized.specialty, // Persist specialty when provided
          // Set captains array with the first captain
          captains: assignCaptain ? [doc(db, 'members', member.id)] : [],
        }

        // Create a clean object without undefined values
        const cleanedData: Partial<SubKreweFirestoreData> = {}
        for (const key in subkreweDocData) {
          if (subkreweDocData[key] !== undefined) {
            cleanedData[key] = subkreweDocData[key]
          }
        }

        // Add the document to Firestore
        const newSubkreweDoc = await addDoc(subkrewesRef, cleanedData)

        // 2. Update the in-memory store
        const subkrewe = this.convertToSubKrewe(newSubkreweDoc.id, cleanedData as SubKreweFirestoreData, false)
        // Initialize the captains array based on options
        subkrewe.captains = assignCaptain ? [member] : []
        this.subkrewes[newSubkreweDoc.path] = subkrewe

        // 3. Optionally update the member using the members store
        if (assignCaptain) {
          const success = await membersStore.updateMember(member, {
            member_type: 'captain',
            subkrewe: subkrewe,
          })

          if (!success) {
            console.error('Failed to update member as captain')
            // Consider rolling back the subkrewe creation?
          }
        }

        // 4. Create an approval task for administrators (best-effort)
        if (!autoApproved) {
          try {
            await tasksStore.createSubKreweApprovalTask(
              newSubkreweDoc.id,
              subkrewe.name,
              member.id,
              member.name,
            )
          }
          catch (taskErr) {
            console.warn('[SubKrewes] Skipping approval task creation due to error:', taskErr)
            // Non-fatal: continue and return created SubKrewe ID
          }
        }

        // 5. Force reactivity update
        this.updateSubKrewesArrayState()

        return newSubkreweDoc.id
      }
      catch (error) {
        console.error('Error creating SubKrewe:', error)
        return null
      }
    },

    /**
     * Find SubKrewes by expected captain email
     */
    /**
 * Find SubKrewes by expected captain email
 * Fix to correctly handle the nested array of objects structure
 */
    async findSubkrewesByExpectedCaptainEmail(email: string): Promise<SubKrewe[]> {
      try {
        const db = useFirestore()
        const lowerCaseEmail = email.toLowerCase().trim()

        // First, we need to fetch all subkrewes without captains assigned
        // since we can't directly query the nested array structure
        const subkreweCollection = collection(db, 'subkrewes')
        const querySnapshot = await getDocs(subkreweCollection)

        const results: SubKrewe[] = []

        // Then manually filter by checking the expected.captains array for each subkrewe
        querySnapshot.forEach((doc) => {
          const data = doc.data() as SubKreweFirestoreData
          const subkrewe = this.convertToSubKrewe(doc.id, data)
          // Skip subkrewes that already have captains assigned
          if (data.captains && Array.isArray(data.captains) && data.captains.length > 0) {
            return
          }

          // Check if this subkrewe has an expected captain with matching email
          const hasMatchingCaptain = subkrewe.expected?.captains?.some(captain =>
            captain.email.toLowerCase() === lowerCaseEmail,
          )

          if (hasMatchingCaptain) {
            results.push(subkrewe)
          }
        })

        return results
      }
      catch (error) {
        console.error('Error finding SubKrewes by expected captain email:', error)
        return []
      }
    },

    /**
   * Update a SubKrewe's captain
   */
    async updateSubKreweCaptain(
      subkreweId: string,
      memberId: string,
      expectedEmail?: string,
      skipFetch = false,
    ): Promise<boolean> {
      try {
        const membersStore = useMembersStore()

        // 1. Validate the SubKrewe
        let subkrewe: SubKrewe | null

        if (skipFetch && this.subkrewes[`subkrewes/${subkreweId}`]) {
          // Use the cached subkrewe if available
          subkrewe = this.subkrewes[`subkrewes/${subkreweId}`] || null
        }
        else {
          // Otherwise fetch it from Firestore
          subkrewe = await this.fetchSubKrewe(subkreweId)
        }

        if (!subkrewe) {
          console.warn('SubKrewe not found')
          return false
        }

        // 2. If expectedEmail is provided, verify it matches one of the expected captains
        if (expectedEmail && subkrewe.expected && subkrewe.expected.captains) {
          const expectedCaptain = subkrewe.expected.captains.find(
            captain => captain.email === expectedEmail,
          )

          if (!expectedCaptain) {
            console.error('Expected email does not match any expected captain')
            return false
          }
        }

        // 3. Fetch the member
        let member: Member | null

        if (skipFetch && membersStore.members[`members/${memberId}`]) {
          // Use the cached member if available
          member = membersStore.members[`members/${memberId}`]
        }
        else {
          // Otherwise fetch from Firestore
          member = await membersStore.fetchMember(memberId)
        }

        if (!member) {
          console.warn('Member not found')
          return false
        }

        // 4. Use assignCaptainToSubKrewe to update all relationships properly
        return await this.assignCaptainToSubKrewe(subkrewe, member)
      }
      catch (error) {
        console.error('Error updating SubKrewe captain:', error)
        return false
      }
    },
    /**
    * Ensure a member who is a captain is properly synchronized as the captain of their subkrewe
    */
    async ensureCaptainSynchronized(subkrewe: SubKrewe, captainMember: Member): Promise<boolean> {
      try {
        // If the member isn't actually a captain, nothing to do
        if (captainMember.member_type !== 'captain') {
          console.error('Member is not a captain')
          return false
        }

        // Check if the captain is already in the list
        const isCaptainInList = subkrewe.captains
          && subkrewe.captains.some(c => c.id === captainMember.id)

        // If already synchronized, nothing to do
        if (isCaptainInList) {
          return true
        }

        // Add this captain to the subkrewe's captains list
        return await this.assignCaptainToSubKrewe(subkrewe, captainMember)
      }
      catch (error) {
        console.error('Error ensuring captain synchronized:', error)
        return false
      }
    },

    /**
     * Delete a SubKrewe and update all member relationships
     * This also sends notifications to all members
     * @param subkreweId The ID of the SubKrewe to delete
     * @param reason Optional reason for deletion to show in the notification
     * @returns Promise<boolean> indicating success
     */
    async deleteSubKrewe(subkreweId: string, actingUserId: string, reason?: string): Promise<boolean> {
      try {
        // 1. Get the SubKrewe to check if it exists
        const subkrewe = await this.fetchSubKrewe(subkreweId, true)
        if (!subkrewe) {
          console.error(`Cannot delete SubKrewe: SubKrewe with ID ${subkreweId} not found`)
          return false
        }

        // 2. Get all members of this SubKrewe to notify them
        await this.loadSubkreweMembers(subkrewe)
        const membersToNotify = [...(subkrewe.members || []), ...(subkrewe.captains || [])]

        // 3. Get the acting user's info for the notification
        const membersStore = useMembersStore()
        const actingMember = await membersStore.fetchMember(actingUserId)
        if (!actingMember) {
          console.error(`Cannot find acting member with ID ${actingUserId}`)
          return false
        }

        // 4. Send notifications to all members before deleting
        const { createRemovedFromSubKreweNotification } = useMembershipNotifications()
        const notificationPromises = membersToNotify.map(member =>
          createRemovedFromSubKreweNotification(
            member.id,
            subkrewe.name,
            actingUserId,
            `SubKrewe was deleted by administrator${reason ? `: ${reason}` : '.'}`,
          ),
        )

        await Promise.all(notificationPromises)

        // 5. Update all members to remove their SubKrewe reference
        const memberUpdatePromises = membersToNotify.map((member) => {
          if (member.member_type === 'captain') {
            // Demote captains to regular members
            return membersStore.updateMember(member, {
              member_type: 'member',
              subkrewe: null,
            })
          }
          else {
            // Just remove the SubKrewe reference for regular members
            return membersStore.updateMember(member, {
              subkrewe: null,
            })
          }
        })

        await Promise.all(memberUpdatePromises)

        // 6. Delete the actual SubKrewe document
        const db = useFirestore()
        const subkreweRef = doc(db, 'subkrewes', subkreweId)
        await deleteDoc(subkreweRef)

        // 7. Remove from local state
        this.$patch((state) => {
          const path = `subkrewes/${subkreweId}`
          delete state.subkrewes[path]
        })

        return true
      }
      catch (error) {
        console.error(`Error deleting subkrewe ${subkreweId}:`, error)
        return false
      }
    },

    /**
     * Abandon a SubKrewe as a captain
     * This sets the SubKrewe as abandoned but does not delete it
     * @param subkrewe The SubKrewe to abandon
     * @param actingUserId ID of the captain abandoning the SubKrewe
     * @param reason Optional reason for abandonment
     * @returns Promise<boolean> indicating success
     */
    async abandonSubKrewe(
      subkrewe: SubKrewe,
      actingUserId: string,
      reason?: string,
    ): Promise<boolean> {
      try {
        if (!subkrewe || !subkrewe.id) {
          console.error('Cannot abandon SubKrewe: invalid SubKrewe object')
          return false
        }

        // Verify the user is a captain of this SubKrewe
        const membersStore = useMembersStore()
        const captain = await membersStore.fetchMember(actingUserId)

        if (!captain || captain.member_type !== 'captain'
          || !captain.subkrewe || captain.subkrewe.id !== subkrewe.id) {
          console.error('User is not a captain of this SubKrewe')
          return false
        }

        // Verify this is the only captain and there are no other members
        if (subkrewe.captains && subkrewe.captains.length > 1) {
          console.error('Cannot abandon SubKrewe with multiple captains')
          return false
        }

        // Ensure members are loaded
        await this.loadSubkreweMembers(subkrewe)

        // Check if there are other members besides the captain
        if (subkrewe.members && subkrewe.members.length > 1) {
          console.error('Cannot abandon SubKrewe with active members')
          return false
        }

        // Get Firestore references
        const db = useFirestore()
        const subkreweRef = doc(db, 'subkrewes', subkrewe.id)

        // 1. Mark the SubKrewe as abandoned and explicitly clear captains array
        await updateDoc(subkreweRef, {
          abandoned: true,
          abandoned_at: serverTimestamp(),
          abandoned_by: actingUserId,
          abandonment_reason: reason || 'No reason provided',
          captains: deleteField(), // Explicitly remove captains array
          updated_at: serverTimestamp(),
        })

        // 2. Demote the captain
        await membersStore.updateMember(captain, {
          member_type: 'member',
          subkrewe: null,
        })

        // 3. Update local state
        this.$patch((state) => {
          const path = `subkrewes/${subkrewe.id}`
          if (state.subkrewes[path]) {
            state.subkrewes[path] = {
              ...state.subkrewes[path],
              abandoned: true,
              abandoned_at: new Date(),
              abandoned_by: actingUserId,
              abandonment_reason: reason || 'No reason provided',
              updated_at: new Date(),
              captains: [], // Clear captains array
              members: [], // Clear members array
            }
          }
        })

        return true
      }
      catch (error) {
        console.error(`Error abandoning SubKrewe ${subkrewe.id}:`, error)
        return false
      }
    },

    setupSubscriptions() {
      const membersStore = useMembersStore()

      // Subscribe to subkrewe changes to sync with members
      this.$subscribe((mutation, state) => {
        console.warn('[SubKrewes] Mutation detected:', mutation)
        // Only handle relevant mutations
        if (mutation.type === 'patch object' || mutation.type === 'patch function') {
          // Look for captain changes in subkrewes
          for (const path in state.subkrewes) {
            const subkrewe = state.subkrewes[path]

            // Skip if no captains array
            if (!subkrewe?.captains || !Array.isArray(subkrewe.captains)) continue

            // Initialize tracking array if it doesn't exist
            if (!subkrewe._lastSyncedCaptainIds) {
              subkrewe._lastSyncedCaptainIds = []
            }

            // Process each captain in the array
            for (const captain of subkrewe.captains) {
              // Skip invalid captains
              if (!captain || !captain.id) continue

              // If this captain ID wasn't previously synced, update member
              if (!subkrewe._lastSyncedCaptainIds.includes(captain.id)) {
                // Add to synced IDs to prevent multiple syncs
                subkrewe._lastSyncedCaptainIds.push(captain.id)

                // Find the captain in the members store
                const captainPath = `members/${captain.id}`
                const memberCaptain = membersStore.members[captainPath]

                if (memberCaptain
                  && (memberCaptain.member_type !== 'captain'
                    || !memberCaptain.subkrewe
                    || memberCaptain.subkrewe.id !== subkrewe.id)) {
                  // Update member as captain of this subkrewe
                  membersStore.$patch((state) => {
                    if (state.members[captainPath]) {
                      state.members[captainPath] = {
                        ...state.members[captainPath],
                        member_type: 'captain',
                        subkrewe: subkrewe,
                        updated_at: new Date(),
                      }
                    }
                  })
                }
              }
            }

            // Clean up tracking array by removing IDs that are no longer captains
            if (subkrewe._lastSyncedCaptainIds.length > 0) {
              const currentCaptainIds = subkrewe.captains.map(c => c.id)
              subkrewe._lastSyncedCaptainIds = subkrewe._lastSyncedCaptainIds
                .filter(id => currentCaptainIds.includes(id))
            }
          }
        }
      }, { flush: 'sync' })
    },
    /**
    * Subscribe to SubKrewes relevant to the current user based on their role
    * - Admins: All SubKrewes
    * - Captains: Their specific SubKrewe (by ID)
    * - Members: SubKrewes they belong to
    *
    * @param subkreweId Optional ID to directly subscribe to a specific SubKrewe (for captains)
    * @returns Unsubscribe function
    */
    subscribeToUserSubKrewes(subkreweId?: string): (() => void) | null {
      const user = useCurrentUser().value
      if (!user?.uid) {
        console.warn('[SubKrewes] Cannot subscribe: No authenticated user')
        return null
      }

      const { registerSubscription, hasSubscriptionWithKey } = useSubscriptions()

      // Create a unique key for this subscription
      const subKey = `user-subkrewes-${user.uid}`

      // Check if we already have this subscription
      if (hasSubscriptionWithKey(subKey)) {
        console.warn('[SubKrewes] Subscription to user SubKrewes already exists')
        return null
      }

      // Build the appropriate query based on user role
      const db = useFirestore()
      const membersStore = useMembersStore()
      const memberPath = `members/${user.uid}`
      const currentMember = membersStore.members[memberPath]

      if (!currentMember) {
        console.warn('[SubKrewes] Cannot subscribe: Member not found in store')
        return null
      }

      console.warn(`[SubKrewes] Setting up subscription for user ${user.uid} with role ${currentMember.member_type}`)

      let subkrewesQuery

      if (memberIsAdmin(currentMember)) {
        // Admins see all subkrewes - this remains unchanged
        console.warn('[SubKrewes] Admin user - subscribing to all subkrewes')
        subkrewesQuery = query(
          collection(db, 'subkrewes'),
          orderBy('name', 'asc'),
        )
      }
      else if (currentMember.member_type === 'captain' || subkreweId) {
        // If we have a direct subkreweId (preferred) or we're a captain, use it
        // First priority: explicitly provided subkreweId
        // Second priority: member's subkrewe ID if they're a captain
        const targetSubkreweId = subkreweId || (currentMember.subkrewe && typeof currentMember.subkrewe === 'object'
          ? currentMember.subkrewe.id
          : (typeof currentMember.subkrewe === 'string' ? currentMember.subkrewe : null))

        if (targetSubkreweId) {
          console.warn(`[SubKrewes] Captain or direct subscription - subscribing to subkrewe: ${targetSubkreweId}`)

          // Use a direct document reference for the specific subkrewe
          return this.subscribeToSubKrewe(targetSubkreweId)
        }
        else {
          console.warn('[SubKrewes] Cannot subscribe: No valid subkrewe ID found')
          // Fallback to empty query
          subkrewesQuery = query(
            collection(db, 'subkrewes'),
            where('name', '==', 'NO_RESULTS_QUERY'), // No results
          )
        }
      }
      else {
        // Regular members see the subkrewe they belong to
        if (currentMember?.subkrewe) {
          try {
            const memberSubkreweId = typeof currentMember.subkrewe === 'string'
              ? currentMember.subkrewe
              : currentMember.subkrewe.id

            if (memberSubkreweId) {
              console.warn(`[SubKrewes] Member user - subscribing to subkrewe: ${memberSubkreweId}`)
              // Use direct document subscription for member's subkrewe too
              return this.subscribeToSubKrewe(memberSubkreweId)
            }
          }
          catch (error) {
            console.error('[SubKrewes] Error creating member query:', error)

            // Fallback to empty query
            subkrewesQuery = query(
              collection(db, 'subkrewes'),
              where('name', '==', 'NO_RESULTS_QUERY'),
            )
          }
        }
        else {
          // If no subkrewe, create an empty query (will return no results)
          console.warn('[SubKrewes] Member without subkrewe - creating empty query')
          subkrewesQuery = query(
            collection(db, 'subkrewes'),
            where('name', '==', 'NO_RESULTS_QUERY'),
          )
        }
      }

      // This block only runs for admins or cases where we couldn't get a direct ID
      // Set up the subscription with includeMetadataChanges: true
      const unsubscribe = onSnapshot(
        subkrewesQuery,
        { includeMetadataChanges: true }, // Critical for catching server-side changes
        (snapshot: QuerySnapshot<DocumentData>) => {
          console.warn('[SubKrewes] Subscription update received:',
            snapshot.docChanges().length, 'changes',
            snapshot.metadata.hasPendingWrites ? '(local)' : '(server)')

          snapshot.docChanges().forEach((change) => {
            const docId = change.doc.id
            const docPath = `subkrewes/${docId}`
            const data = change.doc.data() as SubKreweFirestoreData

            if (change.type === 'added' || change.type === 'modified') {
              // Convert to SubKrewe object and add to state
              const subkrewe = this.convertToSubKrewe(docId, data)
              this.$patch((state) => {
                // Preserve existing members and captains arrays when updating
                const existing = state.subkrewes[docPath]
                if (existing) {
                  subkrewe.members = existing.members
                  subkrewe.captains = existing.captains?.length ? existing.captains : subkrewe.captains
                }
                state.subkrewes[docPath] = subkrewe
              })
            }
            else if (change.type === 'removed') {
              // Remove from state
              this.$patch((state) => {
                delete state.subkrewes[docPath]
              })
              console.warn(`[SubKrewes] Removed subkrewe with id ${docId}`)
            }
          })

          // Force reactivity update after changes
          this.updateSubKrewesArrayState()
        },
        (error) => {
          console.error('[SubKrewes] Error in subscription:', error)
        },
      )

      // Register the subscription with useSubscriptions
      const category = 'subkrewe-user'
      registerSubscription(unsubscribe, category, subKey)

      console.warn(`[SubKrewes] Successfully registered subscription with key: ${subKey}`)

      return unsubscribe
    },
    /**
    * Subscribe to a specific SubKrewe by ID
    * @param subkreweId ID of the SubKrewe to subscribe to
    * @returns Unsubscribe function
    */
    subscribeToSubKrewe(subkreweId: string): (() => void) | null {
      if (!subkreweId) {
        console.error('[SubKrewes] Cannot subscribe: Invalid subkreweId')
        return null
      }

      const { registerSubscription, hasSubscriptionWithKey, clearSubscriptionByKey } = useSubscriptions()
      const db = useFirestore()

      // Create a more unique subscription key to avoid conflicts
      const subKey = `subkrewe-direct-${subkreweId}`

      // If we already have this subscription, clear it first to prevent duplicates
      if (hasSubscriptionWithKey(subKey)) {
        console.warn(`[SubKrewes] Replacing existing subscription for SubKrewe ${subkreweId}`)
        clearSubscriptionByKey(subKey)
      }

      console.warn(`[SubKrewes] Setting up direct subscription for SubKrewe: ${subkreweId}`)

      // Create document reference
      const subkreweRef = doc(db, 'subkrewes', subkreweId)

      try {
        // Set up the subscription with includeMetadataChanges: true
        const unsubscribe = onSnapshot(
          subkreweRef,
          { includeMetadataChanges: true },
          (docSnapshot) => {
            if (docSnapshot.exists()) {
              const docId = docSnapshot.id
              const docPath = `subkrewes/${docId}`
              const data = docSnapshot.data() as SubKreweFirestoreData

              // Convert to SubKrewe object and add to state
              const subkrewe = this.convertToSubKrewe(docId, data)

              // Update the store with the new data
              this.$patch((state) => {
                // Preserve existing members and captains arrays when updating
                const existing = state.subkrewes[docPath]
                if (existing) {
                  subkrewe.members = existing.members
                  subkrewe.captains = existing.captains?.length ? existing.captains : subkrewe.captains
                }
                state.subkrewes[docPath] = subkrewe
              })

              // Force reactivity update after changes
              this.updateSubKrewesArrayState()

              console.warn(`[SubKrewes] Updated specific subkrewe: ${subkreweId}, approval: ${subkrewe.approved}`)
            }
            else {
              console.warn(`[SubKrewes] Subscription received non-existent subkrewe ${subkreweId}`)
            }
          },
          (error) => {
            console.error(`[SubKrewes] Error in subscription for ${subkreweId}:`, error)
          },
        )

        // Register the subscription with useSubscriptions
        const category = `subkrewe-${subkreweId}`
        registerSubscription(unsubscribe, category, subKey)

        console.warn(`[SubKrewes] Successfully registered direct subscription for ${subkreweId} with key: ${subKey}`)

        return unsubscribe
      }
      catch (error) {
        console.error(`[SubKrewes] Failed to set up subscription for SubKrewe ${subkreweId}:`, error)
        return null
      }
    },

    /**
    * Subscribe to members of a specific SubKrewe
    * @param subkreweId ID of the SubKrewe to subscribe to
    * @returns Unsubscribe function
    */
    subscribeToSubKreweMembers(subkreweId: string): (() => void) | null {
      if (!subkreweId) return null

      const { registerSubscription } = useSubscriptions()
      const db = useFirestore()
      const membersStore = useMembersStore()

      // Get current member to determine visibility constraints
      const user = useCurrentUser().value
      if (!user?.uid) return null

      // Check the current user's role
      const memberPath = `members/${user.uid}`
      const currentMember = membersStore.members[memberPath]
      const isAdminOrCaptain = currentMember
        && (memberIsAdmin(currentMember)
          || (currentMember.member_type === 'captain'
            && currentMember.subkrewe?.id === subkreweId))

      // Create a reference to the SubKrewe document
      const subkreweRef = doc(db, 'subkrewes', subkreweId)
      const subkrewePath = `subkrewes/${subkreweId}`

      console.warn(`[SubKrewes] Setting up member subscription for ${subkreweId} as ${isAdminOrCaptain ? 'admin/captain' : 'regular member'}`)

      // Build the query based on user role
      let membersQuery: Query

      if (isAdminOrCaptain) {
        // Admins and captains can see all members
        membersQuery = query(
          collection(db, 'members'),
          where('subkrewe', '==', subkreweRef),
          orderBy('name'),
        )
      }
      else {
        // Regular members can only see themselves and captains
        membersQuery = query(
          collection(db, 'members'),
          and(
            where('subkrewe', '==', subkreweRef),
            where('member_type', '==', 'captain'),
          ),
          orderBy('name'),
        )
      }

      // Set up subscription
      const unsubscribe = onSnapshot(membersQuery, (snapshot) => {
        console.warn(`[SubKrewes] Member snapshot update for ${subkreweId} with ${snapshot.size} total members, ${snapshot.docChanges().length} changes`)

        // Get or ensure the SubKrewe exists in the store
        const subkrewe = this.subkrewes[subkrewePath]

        if (!subkrewe) {
          console.warn(`[SubKrewes] SubKrewe ${subkreweId} not found in store, will update when available`)
          // Don't return - we'll store the members and update when SubKrewe loads
          // This can happen when members subscription loads before SubKrewe subscription
        }

        // Rebuild the entire members array from the snapshot to avoid issues with incremental updates
        const updatedMembers: Member[] = []

        // Process all documents in the snapshot
        snapshot.forEach((docSnapshot) => {
          const data = docSnapshot.data() as MemberFirestoreData
          const member = membersStore.convertToMember(docSnapshot.id, data, false)

          // Update member in store first
          membersStore.$patch((state) => {
            // Make sure the member has the correct subkrewe reference
            if (subkrewe) {
              member.subkrewe = subkrewe
            }
            const memberPath = `members/${docSnapshot.id}`
            state.members[memberPath] = member
          })

          // Add to our members array
          updatedMembers.push({
            ...member,
            subkrewe: subkrewe || member.subkrewe,
          })
        })

        // Update the SubKrewe's members array if the SubKrewe exists
        // If it doesn't exist yet, the members will be loaded when the SubKrewe is loaded
        this.$patch((state) => {
          if (state.subkrewes[subkrewePath]) {
            state.subkrewes[subkrewePath].members = updatedMembers
            console.warn(`[SubKrewes] Updated members array for ${subkreweId}: ${updatedMembers.length} members`)
          }
          else {
            console.warn(`[SubKrewes] SubKrewe ${subkreweId} not in store yet, members will be loaded later`)
          }
        })
      }, (error) => {
        console.error(`[SubKrewes] Error in members subscription for ${subkreweId}:`, error)
      })

      // Register subscription
      registerSubscription(unsubscribe, `subkrewe-members-${subkreweId}`)

      return unsubscribe
    },

    /**
    * Subscribe to all available SubKrewes for browsing
    * This is separate from subscribeToUserSubKrewes to allow members to browse all SubKrewes
    * @returns Unsubscribe function
    */
    subscribeToAvailableSubKrewes(): (() => void) | null {
      console.warn('[SubKrewesStore] Setting up subscription to available SubKrewes')

      // Require an authenticated user for consistency with other subscriptions and test expectations
      const user = useCurrentUser().value
      if (!user?.uid) {
        console.warn('[SubKrewesStore] Cannot subscribe to available SubKrewes: No authenticated user')
        return null
      }

      // Get the necessary dependencies
      const { registerSubscription } = useSubscriptions()
      const db = useFirestore()

      // Query for available subkrewes - only get active, approved subkrewes that are not bands
      const subkrewesQuery = query(
        collection(db, 'subkrewes'),
        where('approved', '==', 'Yes'),
        orderBy('name', 'asc'),
      )

      // Set up the subscription with includeMetadataChanges: true
      const unsubscribe = onSnapshot(
        subkrewesQuery,
        { includeMetadataChanges: true }, // Important for cache/server distinction
        (snapshot) => {
          console.warn('[SubKrewesStore] Available SubKrewes subscription update received:',
            snapshot.docChanges().length, 'changes')

          snapshot.docChanges().forEach((change) => {
            const docId = change.doc.id
            const docPath = `subkrewes/${docId}`
            const data = change.doc.data() as SubKreweFirestoreData

            if (change.type === 'added' || change.type === 'modified') {
              // Convert to SubKrewe object and add to state
              const subkrewe = this.convertToSubKrewe(docId, data)
              this.$patch((state) => {
                // Preserve existing members and captains arrays when updating
                const existing = state.subkrewes[docPath]
                if (existing) {
                  subkrewe.members = existing.members
                  subkrewe.captains = existing.captains?.length ? existing.captains : subkrewe.captains
                }
                state.subkrewes[docPath] = subkrewe
              })

              // console.warn(`[SubKrewesStore] ${change.type === 'added' ? 'Added' : 'Updated'} available SubKrewe: ${subkrewe.name}`)
            }
            else if (change.type === 'removed') {
              // Remove from state
              this.$patch((state) => {
                delete state.subkrewes[docPath]
              })
              console.warn(`[SubKrewesStore] Removed available SubKrewe with id ${docId}`)
            }
          })

          // Force reactivity update
          this.updateSubKrewesArrayState()
        },
        (error) => {
          console.error('[SubKrewes] Error in available subscription:', error)
        },
      )

      // Register the subscription in the centralized subscription manager
      registerSubscription(unsubscribe, 'available-subkrewes')

      return unsubscribe
    },
  },
},
)

function ensureNumber(value: number | string | undefined): number {
  if (typeof value === 'string') {
    const parsed = parseInt(value, 10)
    return isNaN(parsed) ? 0 : parsed
  }
  else if (typeof value === 'number') {
    return value
  }
  return 0
}
